import AirIcon from '@mui/icons-material/Air';
import PlaceIcon from '@mui/icons-material/PlaceOutlined';
import SettingsIcon from '@mui/icons-material/Settings';
import SwapHorizOutlined from '@mui/icons-material/SwapHorizOutlined';
import ThermostatIcon from '@mui/icons-material/Thermostat';
import TimelineIcon from '@mui/icons-material/Timeline';
import { Box, Button, Card, Chip, Stack, StackProps, SxProps, Theme, Tooltip, Typography } from '@mui/material';
import { isNil } from 'lodash';
import { ReactNode, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';

import { useSpotterModeChangeStatus } from '@/api/device/useSpotterModeChangeStatus';
import {
    Device,
    DeviceHullType,
    DeviceLatestData,
    MAX_BATTERY_LEVEL,
    MAX_CELLULAR_SIGNAL_LEVEL,
    useDeviceBatteryStatus,
    useDeviceCellularSignalStatus,
    useDeviceSdCardStatus,
    useDeviceSignalStatus,
    useDeviceSubscriptionStatus,
} from '@/api/devices';
import { PartitionDataItem } from '@/api/historicalData';
import { selectPreferredWaveSource } from '@/api/historicalData/helpers';
import { telemetryTypeToLabelMap } from '@/api/subscriptionTags';
import {
    pressureIcon,
    sdCardIcon,
    wavePeriodBulkIcon,
    wavePeriodSeaIcon,
    wavePeriodSwellIcon,
    wavesBulkIcon,
    wavesSeaIcon,
    wavesSwellIcon,
} from '@/assets';
import spotterImageHover from '@/assets/map/spotter-yellow-hover.png';
import spotterImageSelected from '@/assets/map/spotter-yellow-selected.png';
import spotterImageYellow from '@/assets/map/spotter-yellow.png';
import scoutHoverIcon from '@/assets/scout-card-hover.svg';
import scoutSelectedIcon from '@/assets/scout-card-selected.svg';
import scoutIcon from '@/assets/scout-card.svg';
import { convert } from '@/common/convert';
import { AlertChip } from '@/components/AlertChip';
import { ErrorIcon, SuccessIcon } from '@/components/StatusIcons';
import { BeaufortNumber } from '@/components/data/BeaufortNumber';
import { DataReadoutGrid, DataReadoutGridProps, ReadoutGridSubcontainer } from '@/components/data/DataReadoutGrid';
import { Datum } from '@/components/data/Datum';
import { DirectionDatum } from '@/components/data/DirectionDatum';
import { DotLevelIndicator } from '@/components/data/DotLevelIndicator';
import { IconData, LabeledData } from '@/components/data/IconData';
import { LatLong } from '@/components/data/LatLong';
import { Timestamp } from '@/components/data/Timestamp';
import { ScoutSummarySection } from '@/components/map/ScoutSummarySection';
import { SensorAlert } from '@/components/map/SensorAlert';
import { SharedSpotterChip } from '@/components/spotter/SharedSpotterIndicators';
import { SmartMooringDataReadouts } from '@/components/spotter/SmartMooringSensorReadouts';
import { TelemetryServiceStatusMessage } from '@/components/spotter/TelemetryServiceStatusMessage';
import { SpotterContextProvider } from '@/context/SpotterContext';
import { useGeofenceDisplayData } from '@/hooks/useGeofenceDisplayData';
import { useIsSharedDevice } from '@/hooks/useIsSharedDevice';
import { spotterIdHash, useSpotterHash } from '@/hooks/useSpotterHash';
import { boldWeight, minorTextGrey } from '@/theme';
import { isScoutDevice } from '@/utils';

export const SELECTED_SPOTTER_SUMMARY_CARD_SX = { border: '2px solid #096ED3', m: '-1px' };
const defaultIcons = { regular: spotterImageYellow, hover: spotterImageHover, selected: spotterImageSelected };
const hullTypeToIconMap: Record<DeviceHullType, { regular: string; hover: string; selected: string }> = {
    spotter: defaultIcons,
    velella: defaultIcons,
    scout: { regular: scoutIcon, hover: scoutHoverIcon, selected: scoutSelectedIcon },
};

const SpotterCardHeader = ({
    device,
    spotterIcon,
    actions,
}: {
    device: Device;
    spotterIcon: string;
    actions: ReactNode;
}) => {
    const isSharedDevice = useIsSharedDevice(device.spotterId);
    const { isSatelliteModeChanging, isCellularModeChanging } = useSpotterModeChangeStatus({
        spotterId: device.spotterId,
    });

    return (
        <Stack
            direction="row"
            sx={{ alignItems: 'start', flexWrap: 'wrap', gap: 0.5, justifyContent: 'space-between' }}
        >
            <Stack direction="row" sx={{ flexWrap: 'wrap', gap: 0.5, alignItems: 'center' }}>
                <Typography
                    data-testid="spotter-summary-card-title"
                    variant="h3"
                    sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 0.5 }}
                >
                    <Box component="img" src={spotterIcon} alt="Spotter" sx={{ height: '30px', py: 0.5 }} />
                    {device.name || device.spotterId}
                    {device.name && device.name !== device.spotterId && (
                        <Box component="span" sx={{ color: minorTextGrey }}>
                            {device.spotterId}
                        </Box>
                    )}
                </Typography>
                {device.currentMode === 'deployment' && <Chip label="Idle mode" color="default" size="small" />}
            </Stack>
            <Stack
                direction="row"
                sx={{
                    gap: 1,
                    ml: 'auto',
                    flexShrink: 0,
                    alignItems: 'center',
                }}
            >
                {(isSatelliteModeChanging || isCellularModeChanging) && (
                    <Stack direction="row" gap={0.5}>
                        <SwapHorizOutlined />
                        <Typography variant="minorLabel" color="textPrimary" sx={{ mt: 0.5, fontWeight: boldWeight }}>
                            Switching modes...
                        </Typography>
                    </Stack>
                )}
                {isSharedDevice && <SharedSpotterChip />}
                {actions}
            </Stack>
        </Stack>
    );
};

export const SpotterSummaryCard = ({
    device,
    isHovered,
    onHoverChange,
}: {
    device: Device;
    isHovered: boolean;
    onHoverChange: (hovered: boolean) => void;
}) => {
    const navigate = useNavigate();
    const hash = useSpotterHash();
    const idHash = spotterIdHash(device.spotterId);
    const cardRef = useRef<HTMLDivElement>(null);

    const { className, spotterIcon } = useMemo(() => {
        const icons = hullTypeToIconMap[device.hullType] ?? defaultIcons;

        if (hash.spotterId === device.spotterId) {
            return { className: 'selected', spotterIcon: icons.selected };
        }
        if (isHovered) {
            return { className: 'hovered', spotterIcon: icons.hover };
        }
        return { spotterIcon: icons.regular };
    }, [hash, device, isHovered]);

    function navigateAway(url: string) {
        // first update the URL to this spotter so that back returns you to the same place,
        // then do the actual navigation requests.
        navigate(`#${idHash}`);
        navigate(url);
    }

    function onClickCard() {
        // double checking we're still on the map; if we're not, it's probably because an earlier click handler
        // already navigated, and we don't want to stick the hash on someone else's path.
        // this test is a little annoying; we have to use window.location, not useLocation(), because the browser
        // has the new pathname before useLocation exposes it, and we don't have it in time here. this means
        // we don't get the router's '/' normalization.
        if (['', '/'].includes(window.location.pathname)) {
            navigate(`#${idHash}`);
        }
    }

    return (
        <SpotterContextProvider spotterId={device.spotterId}>
            <Card
                variant="outlined"
                id={idHash}
                ref={cardRef}
                className={className}
                onPointerEnter={() => onHoverChange(true)}
                onPointerLeave={() => onHoverChange(false)}
                sx={{
                    boxSizing: 'border-box',
                    border: '1px solid',
                    borderColor: 'divider',
                    '&.selected': SELECTED_SPOTTER_SUMMARY_CARD_SX,
                    '&.hovered': { border: '2px solid #7CB9F6', m: '-1px' },
                    p: 1,
                    scrollMargin: 2,
                }}
                onClick={onClickCard}
            >
                <SpotterCardHeader
                    device={device}
                    spotterIcon={spotterIcon}
                    actions={
                        <>
                            <Tooltip title="Spotter settings">
                                <Button
                                    sx={{ padding: '6px', minWidth: 'unset' }}
                                    variant="outlined"
                                    onClick={() => navigateAway(`/spotter/${device.spotterId}#configuration`)}
                                >
                                    <SettingsIcon />
                                </Button>
                            </Tooltip>
                            <Button
                                variant="contained"
                                onClick={() => navigateAway(`/spotter/${device.spotterId}#observation-data`)}
                            >
                                <TimelineIcon sx={{ ml: -0.5, mr: 0.5 }} /> More data
                            </Button>
                        </>
                    }
                />
                <SpotterSensorSummary sx={{ pt: 1, pb: 0.5 }} device={device} dataColumnCount="auto" />
                <SpotterSystemHealthSummary sx={{ pb: 1 }} device={device} dataColumnCount="auto" />
                <ScoutSummarySection sx={{ pb: 1 }} device={device} dataColumnCount="auto" />
            </Card>
        </SpotterContextProvider>
    );
};

export const SYSTEM_DATA_READOUT_SX: SxProps<Theme> = {
    border: '1px solid rgba(192, 192, 192, 0.8)',
    padding: 1.5,
    backgroundColor: 'initial',
};

export const SpotterSystemHealthSummary = ({
    device,
    dataColumnCount,
    ...props
}: {
    device: Device;
    dataColumnCount?: DataReadoutGridProps['columns'];
} & StackProps) => {
    const { spotterId, cellularData, hullType } = device;
    const geofenceData = useGeofenceDisplayData(device);
    const { isBreached = false, breachedByMeters = 0 } = geofenceData ?? {};

    const { data: batteryStatus } = useDeviceBatteryStatus({ spotterId });
    const { data: signalStatus } = useDeviceSignalStatus({ spotterId });
    const { data: sdCardStatus } = useDeviceSdCardStatus({ spotterId });
    const { data: cellularSignalStatus } = useDeviceCellularSignalStatus({ spotterId });
    const { data: subscriptionStatus } = useDeviceSubscriptionStatus({ spotterId });
    const { isLateToCheckin, lastUpdateSource, lastUpdated } = signalStatus ?? {};
    const currentLocation = device.track?.[0];
    const showLocation = !isScoutDevice(hullType) && !!currentLocation;
    const showSdCardStatus = sdCardStatus && sdCardStatus.status === 'enabled';
    const showCellularStatus =
        device.cellularData?.hasCellular &&
        device.cellularData?.cellularEnabled &&
        subscriptionStatus?.cellEnabled &&
        cellularData?.latestCellularSignalMetric &&
        cellularSignalStatus;

    return (
        <>
            <DataReadoutGrid {...props} columns={dataColumnCount}>
                {showLocation && (
                    <IconData
                        Icon={PlaceIcon}
                        label="Location"
                        timestamp={currentLocation.timestamp}
                        sx={SYSTEM_DATA_READOUT_SX}
                    >
                        <LatLong coord={currentLocation} />
                        {isBreached && (
                            <AlertChip
                                severity="error"
                                label={
                                    <>
                                        <Datum
                                            value={breachedByMeters}
                                            sourceUnit="m"
                                            type="length"
                                            maxDecimalPlaces={0}
                                            sx={{
                                                fontWeight: (theme) => theme.typography.h6.fontWeight,
                                                fontSize: (theme) => theme.typography.h6.fontSize,
                                            }}
                                        />{' '}
                                        out of geofence range
                                    </>
                                }
                            />
                        )}
                    </IconData>
                )}
                {batteryStatus && (
                    <IconData
                        Icon={batteryStatus.StatusIcon}
                        label="Battery"
                        sx={SYSTEM_DATA_READOUT_SX}
                        timestamp={batteryStatus.timestamp}
                    >
                        {batteryStatus?.level && (
                            <DotLevelIndicator
                                level={batteryStatus.level}
                                maxLevel={MAX_BATTERY_LEVEL}
                                color={batteryStatus.color}
                            />
                        )}
                        {batteryStatus.error && <AlertChip severity="error" label={batteryStatus.error.description} />}
                    </IconData>
                )}

                {showSdCardStatus && (
                    <IconData
                        src={sdCardIcon}
                        label="SD card"
                        timestamp={sdCardStatus.timestamp}
                        sx={SYSTEM_DATA_READOUT_SX}
                    >
                        {sdCardStatus.severity === 'success' ? (
                            <>
                                {sdCardStatus.StatusIcon}
                                <Typography>{sdCardStatus.label}</Typography>
                            </>
                        ) : (
                            <AlertChip severity={sdCardStatus.severity} label={sdCardStatus.label} />
                        )}
                    </IconData>
                )}
                {showCellularStatus && (
                    <IconData
                        Icon={cellularSignalStatus.StatusIcon}
                        label="Cellular signal"
                        sx={SYSTEM_DATA_READOUT_SX}
                        timestamp={cellularSignalStatus.timestamp}
                    >
                        <Typography>{cellularSignalStatus.description}</Typography>
                        {cellularSignalStatus.level > 0 && (
                            <DotLevelIndicator
                                level={cellularSignalStatus.level}
                                maxLevel={MAX_CELLULAR_SIGNAL_LEVEL}
                                color={cellularSignalStatus.color}
                            />
                        )}
                        {cellularSignalStatus.error && (
                            <Box>
                                <AlertChip severity="error" label={cellularSignalStatus.error.description} />
                            </Box>
                        )}
                    </IconData>
                )}
            </DataReadoutGrid>
            <Stack gap={0.5}>
                {lastUpdated && (
                    <Stack direction="row" sx={{ gap: 0.5, alignItems: 'center' }}>
                        {isLateToCheckin ? <ErrorIcon fontSize="inherit" /> : <SuccessIcon fontSize="inherit" />}
                        <Typography variant="minorLabel" color="textPrimary">
                            Last checked in{' '}
                            <strong>
                                <Timestamp type="distanceToNow" value={lastUpdated} />
                                {lastUpdateSource && <> via {telemetryTypeToLabelMap[lastUpdateSource]}</>}
                            </strong>
                        </Typography>
                    </Stack>
                )}
                <TelemetryServiceStatusMessage spotterId={spotterId} />
            </Stack>
        </>
    );
};

export const PartitionReadouts = ({
    partitions,
    timestamp,
}: {
    partitions: [PartitionDataItem, PartitionDataItem] | null | undefined;
    timestamp: string;
}) => {
    if (!partitions?.[0]) {
        return false;
    }

    const [swell, sea] = partitions;

    return (
        <>
            <LabeledData label="Sea" timestamp={timestamp} dataSx={{ gap: 1 }}>
                <IconData src={wavesSeaIcon} tooltip="Sea significant wave height">
                    <Datum value={sea.significantWaveHeight} sourceUnit="m" type="distance" />
                </IconData>
                <IconData src={wavePeriodSeaIcon} tooltip="Mean sea wave period">
                    <Datum value={sea.meanPeriod} sourceUnit="s" type="time" />
                </IconData>
                <DirectionDatum type="source" heading={sea.meanDirection} tooltip="Mean sea wave direction" />
            </LabeledData>
            <LabeledData label="Swell" timestamp={timestamp} dataSx={{ gap: 1 }}>
                <IconData src={wavesSwellIcon} tooltip="Swell significant wave height">
                    <Datum value={swell.significantWaveHeight} sourceUnit="m" type="distance" />
                </IconData>
                <IconData src={wavePeriodSwellIcon} tooltip="Mean swell period">
                    <Datum value={swell.meanPeriod} sourceUnit="s" type="time" />
                </IconData>
                <DirectionDatum type="source" heading={swell.meanDirection} tooltip="Mean swell direction" />
            </LabeledData>
        </>
    );
};

export const SpotterSensorSummary = ({
    device,
    dataColumnCount,
    ...props
}: {
    device: Device | DeviceLatestData;
    dataColumnCount?: DataReadoutGridProps['columns'];
} & StackProps) => {
    const spotterId = 'spotterId' in device ? device.spotterId : undefined;
    const prioritySensorPortalRef = useRef(null);

    // Select preferred wave source: HDR vs embedded for full Device, or device.waves for DeviceLatestData
    const selectedWaves = useMemo(() => {
        if (!('hdrWaves' in device)) {
            return device.waves;
        }
        const processingRateMins = device.cellularData?.cellularSettings?.processingRateMins ?? 30;
        return selectPreferredWaveSource(device.hdrWaves, device.embeddedWaves, processingRateMins) ?? device.waves;
    }, [device]);

    const showPartitionWaves = selectedWaves && 'partitions' in selectedWaves && selectedWaves.partitions;

    return (
        <DataReadoutGrid {...props} columns={dataColumnCount}>
            <ReadoutGridSubcontainer ref={prioritySensorPortalRef} />
            {selectedWaves && !isNil(selectedWaves.significantWaveHeight) && (
                <LabeledData label="Waves" timestamp={selectedWaves.timestamp} dataSx={{ gap: 1 }}>
                    <IconData src={wavesBulkIcon} tooltip="Bulk significant wave height">
                        <Datum value={selectedWaves.significantWaveHeight} sourceUnit="m" type="distance" />
                    </IconData>
                    <IconData src={wavePeriodBulkIcon} tooltip="Mean bulk wave period">
                        <Datum value={selectedWaves.meanPeriod} sourceUnit="s" type="time" />
                    </IconData>
                    <DirectionDatum
                        type="source"
                        heading={selectedWaves.meanDirection}
                        tooltip="Mean bulk wave direction"
                    />
                    <SensorAlert
                        spotterId={spotterId}
                        alertType="spotter_threshold_significantwaveheight"
                        currentValue={selectedWaves.significantWaveHeight}
                        currentValueUnit="m"
                    />
                    <SensorAlert
                        spotterId={spotterId}
                        alertType="spotter_threshold_meanwaveperiod"
                        currentValue={selectedWaves.meanPeriod}
                        currentValueUnit="s"
                    />
                    <SensorAlert
                        spotterId={spotterId}
                        alertType="spotter_threshold_peakwaveperiod"
                        currentValue={selectedWaves.peakPeriod}
                        currentValueUnit="s"
                    />
                </LabeledData>
            )}
            {showPartitionWaves && (
                <PartitionReadouts partitions={selectedWaves.partitions} timestamp={selectedWaves.timestamp} />
            )}
            {device.wind && (
                <LabeledData
                    label="Wind"
                    timestamp={'location' in device.wind ? device.wind.location.timestamp : undefined}
                    dataSx={{ gap: 1 }}
                >
                    <IconData Icon={AirIcon} tooltip="Mean wind speed">
                        <Datum value={device.wind.speed} sourceUnit="m/s" type="speed" />
                    </IconData>
                    <DirectionDatum type="source" heading={device.wind.direction} tooltip="Mean wind direction" />
                    <BeaufortNumber windSpeedKnots={convert(device.wind.speed).from('m/s').to('knots')} />
                    <SensorAlert
                        spotterId={spotterId}
                        alertType="spotter_threshold_windspeed"
                        currentValue={device.wind.speed}
                        currentValueUnit="m/s"
                    />
                </LabeledData>
            )}
            {device.airPressure && (
                <LabeledData label="Air pressure" timestamp={device.airPressure.location.timestamp} dataSx={{ gap: 1 }}>
                    <IconData src={pressureIcon}>
                        <Datum value={device.airPressure.pressure} type="airPressure" sourceUnit="hPa" />
                    </IconData>
                    <SensorAlert
                        spotterId={spotterId}
                        alertType="spotter_threshold_barometricpressure"
                        currentValue={device.airPressure.pressure}
                        currentValueUnit="hPa"
                    />
                </LabeledData>
            )}
            {device.surfaceTemperature && (
                <LabeledData
                    label="Surface temperature"
                    timestamp={device.surfaceTemperature.location.timestamp}
                    dataSx={{ gap: 1 }}
                >
                    <IconData Icon={ThermostatIcon}>
                        <Datum value={device.surfaceTemperature.degrees} type="temperature" sourceUnit="C" />
                    </IconData>
                    <SensorAlert
                        spotterId={spotterId}
                        alertType="spotter_threshold_seasurfacetemperature"
                        currentValue={device.surfaceTemperature.degrees}
                        currentValueUnit="C"
                    />
                </LabeledData>
            )}
            {'isSmartMooring' in device && (
                <SmartMooringDataReadouts device={device} prioritySensorPortal={prioritySensorPortalRef.current} />
            )}
        </DataReadoutGrid>
    );
};

export const HistoricalSpotterSensorSummary = ({
    device,
    dataColumnCount,
    ...props
}: {
    device: Device | DeviceLatestData;
    dataColumnCount?: DataReadoutGridProps['columns'];
} & StackProps) => {
    const prioritySensorPortalRef = useRef(null);

    return (
        <DataReadoutGrid {...props} columns={dataColumnCount}>
            <ReadoutGridSubcontainer ref={prioritySensorPortalRef} />
            {'isSmartMooring' in device && (
                <SmartMooringDataReadouts
                    device={device}
                    prioritySensorPortal={prioritySensorPortalRef.current}
                    sampleTime="timelineCursor"
                />
            )}
        </DataReadoutGrid>
    );
};
